import { useContext } from 'react';
import { GridContext } from 'contexts/GridContext';
import { ValidationContext } from 'contexts/ValidationContext';
import { ViewContext } from 'components/lib';
import { UMCoordinateSystemSelector } from '@urbanmetrix/um-kit';
import type { CoordinateSystemPreference } from 'utils/coordinateSystemManager';
import { InlineInfoIcon } from 'components/icons';

interface FileCoordinatesProps {
  coordinateBase?: string;
  fileFieldKey?: string;
  linkedFileFieldKeys?: string[];
  compact?: boolean;
  value?: CoordinateSystemPreference;
  onChange?: (nextValue: CoordinateSystemPreference) => void;
}

const FileCoordinates = ({ fileFieldKey, linkedFileFieldKeys, value, onChange }: FileCoordinatesProps) => {
  const { gridParams, coordinateSystemPreference, setCoordinateSystemPreference, setGridParams } =
    useContext(GridContext);
  const { setIsGridCoordinateSystemValid } = useContext(ValidationContext);
  const viewContext = useContext(ViewContext);
  const selectorValue = value ?? coordinateSystemPreference;
  const handleCoordinateSystemChange = onChange ?? setCoordinateSystemPreference;

  const handleSelectorChange = (nextValue: CoordinateSystemPreference) => {
    handleCoordinateSystemChange(nextValue);

    if (!fileFieldKey) {
      return;
    }

    const nextX = nextValue.mode === 'custom' ? Number(nextValue.centerX) || 0 : 0;
    const nextY = nextValue.mode === 'custom' ? Number(nextValue.centerY) || 0 : 0;
    const nextZ = nextValue.mode === 'custom' ? Number(nextValue.centerZ) || 0 : 0;
    const isZeroCoords = nextX === 0 && nextY === 0 && nextZ === 0;
    const fileKeys = linkedFileFieldKeys?.length ? linkedFileFieldKeys : [fileFieldKey];
    const keysWithFiles = fileKeys.filter((key): key is string => Boolean(key) && Boolean(gridParams?.[key]));

    if (isZeroCoords && keysWithFiles.length > 0) {
      setGridParams(Object.fromEntries(keysWithFiles.map((key) => [key, null])) as Record<string, null>);
      viewContext?.notification?.show?.(
        keysWithFiles.length > 1
          ? 'Files removed: with local coordinates 0,0,0 you must upload them again.'
          : 'File removed: with local coordinates 0,0,0 you must upload it again.',
        'warning',
        true,
        'toast'
      );
    }
  };

  return (
    <div style={{ marginBottom: '5px' }}>
      <UMCoordinateSystemSelector
        title="Coordinates System:"
        fontSize={14}
        alignRadios={true}
        helpIcon={<InlineInfoIcon />}
        value={selectorValue}
        localTooltip="Move your 3D model to its exact Swiss coordinates (X, Y, Z) inside your 3D modeling software before exporting it."
        customCoordsTooltip="Place your 3D model using a custom origin point (0,0,0) in your 3D modeling software before exporting it (e.g. the corner of your Property Area). Enter here the position and orientation of the custom reference system in Swiss coordinates (X, Y, Z, Angle)."
        onChange={handleSelectorChange}
        onValidationChange={({ isValid }) => {
          setIsGridCoordinateSystemValid(isValid);
        }}
      />
    </div>
  );
};

export default FileCoordinates;
